import React, { useState, useEffect } from "react";
import { Box, Alert, Text, Stack } from "@mantine/core";
import { marked, MarkedOptions } from "marked";
import DOMPurify, { Config } from "dompurify";

export interface MarkdownContentBlockProps {
  /** Markdown content to display */
  markdownContent: string | null | undefined;

  /** Additional CSS class name for styling */
  className?: string;

  /** Custom marked options */
  markedOptions?: MarkedOptions;

  /** Custom DOMPurify configuration */
  sanitizeConfig?: Config;

  themeConfig?: any;

  /** Callback fired when markdown content is rendered */
  onRender?: (() => void) | undefined;

  /** Callback fired when markdown processing fails */
  onError?: ((error: string) => void) | undefined;
}

export const MarkdownContentBlock: React.FC<MarkdownContentBlockProps> = ({
  markdownContent,
  className,
  markedOptions,
  sanitizeConfig,
  onRender,
  onError,
}) => {
  const [processedHtml, setProcessedHtml] = useState<string | null>("");
  const [isProcessing, setIsProcessing] = useState(false);

  // Process markdown content
  useEffect(() => {
    const processMarkdown = async () => {
      if (!markdownContent) {
        setProcessedHtml("");
        return;
      }

      setIsProcessing(true);

      try {
        // Configure marked options
        const defaultOptions: MarkedOptions = {
          breaks: true,
          gfm: true,
          //headerIds: false,
          //mangle: false,
          ...markedOptions,
        };

        // Convert markdown to HTML
        const htmlContent = await marked.parse(markdownContent, defaultOptions);

        // Sanitize the HTML
        const defaultSanitizeConfig: Config = {
          ALLOWED_TAGS: [
            "p",
            "div",
            "span",
            "h1",
            "h2",
            "h3",
            "h4",
            "h5",
            "h6",
            "strong",
            "em",
            "u",
            "s",
            "br",
            "hr",
            "ul",
            "ol",
            "li",
            "a",
            "img",
            "table",
            "thead",
            "tbody",
            "tr",
            "th",
            "td",
            "blockquote",
            "pre",
            "code",
            "small",
            "sub",
            "sup",
          ],
          ALLOWED_ATTR: [
            "href",
            "src",
            "alt",
            "title",
            "class",
            "id",
            "target",
            "rel",
            "width",
            "height",
          ],
          ALLOW_DATA_ATTR: false,
          FORBID_TAGS: ["script", "object", "embed", "form", "input"],
          FORBID_ATTR: ["onclick", "onload", "onerror", "onmouseover"],
          ...sanitizeConfig,
        };

        const sanitizedHtml = DOMPurify.sanitize(
          htmlContent,
          defaultSanitizeConfig
        );

        setProcessedHtml(sanitizedHtml);
        onRender?.();
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Markdown processing failed";
        console.error("MarkdownContentBlock processing error:", error);
        setProcessedHtml(null);
        onError?.(errorMessage);
      } finally {
        setIsProcessing(false);
      }
    };

    processMarkdown();
  }, [markdownContent, markedOptions, sanitizeConfig, onRender, onError]);

  // Show processing state
  if (isProcessing) {
    return (
      <Box
        className={`markdown-content-block markdown-content-block--processing ${
          className || ""
        }`}
        h="100%"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "var(--mantine-spacing-lg)",
        }}
      >
        <Text
          size="sm"
          c="dimmed"
          className="markdown-content-block__processing-message"
          ta="center"
        >
          Processing markdown...
        </Text>
      </Box>
    );
  }

  // Handle processing errors
  if (processedHtml === null) {
    return (
      <Box
        className={`markdown-content-block markdown-content-block--error ${
          className || ""
        }`}
        h="100%"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "var(--mantine-spacing-lg)",
        }}
      >
        <Stack align="center" gap="md" maw={400}>
          <Alert
            color="red"
            title="Markdown Content Error"
            className="markdown-content-block__error-alert"
          >
            <Text
              size="sm"
              className="markdown-content-block__error-message"
              ta="center"
            >
              Unable to display markdown content due to processing failure.
            </Text>
          </Alert>
        </Stack>
      </Box>
    );
  }

  // Handle empty content
  if (!processedHtml.trim()) {
    return (
      <Box
        className={`markdown-content-block markdown-content-block--empty ${
          className || ""
        }`}
        h="100%"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "var(--mantine-spacing-lg)",
        }}
      >
        <Text
          size="sm"
          c="dimmed"
          className="markdown-content-block__empty-message"
          ta="center"
        >
          No markdown content to display
        </Text>
      </Box>
    );
  }

  return (
    <Box
      className={`markdown-content-block ${className || ""}`}
      h="100%"
      style={{
        overflow: "auto",
        padding: "var(--mantine-spacing-md)",
      }}
    >
      <Box
        className="markdown-content-block__content"
        dangerouslySetInnerHTML={{ __html: processedHtml }}
        style={{
          // Style markdown content to match theme
          "& h1, & h2, & h3, & h4, & h5, & h6": {
            color: "var(--mantine-color-text)",
            fontFamily: "var(--mantine-font-family-headings)",
            fontWeight: 700,
            lineHeight: "var(--mantine-line-height-xs)",
            marginTop: "var(--mantine-spacing-lg)",
            marginBottom: "var(--mantine-spacing-sm)",
          },
          "& h1": {
            fontSize: "var(--mantine-h1-font-size)",
          },
          "& h2": {
            fontSize: "var(--mantine-h2-font-size)",
          },
          "& h3": {
            fontSize: "var(--mantine-h3-font-size)",
          },
          "& h4": {
            fontSize: "var(--mantine-h4-font-size)",
          },
          "& h5": {
            fontSize: "var(--mantine-h5-font-size)",
          },
          "& h6": {
            fontSize: "var(--mantine-h6-font-size)",
          },
          "& p": {
            color: "var(--mantine-color-text)",
            fontFamily: "var(--mantine-font-family)",
            fontSize: "var(--mantine-font-size-md)",
            lineHeight: "var(--mantine-line-height)",
            marginBottom: "var(--mantine-spacing-sm)",
          },
          "& a": {
            color: "var(--mantine-color-blue-6)",
            textDecoration: "none",
          },
          "& a:hover": {
            textDecoration: "underline",
          },
          "& ul, & ol": {
            paddingLeft: "var(--mantine-spacing-lg)",
            marginBottom: "var(--mantine-spacing-sm)",
          },
          "& li": {
            marginBottom: "var(--mantine-spacing-xs)",
          },
          "& blockquote": {
            borderLeft: `4px solid var(--mantine-color-gray-4)`,
            paddingLeft: "var(--mantine-spacing-md)",
            marginLeft: 0,
            marginBottom: "var(--mantine-spacing-sm)",
            fontStyle: "italic",
            color: "var(--mantine-color-dimmed)",
          },
          "& pre": {
            backgroundColor: "var(--mantine-color-gray-1)",
            padding: "var(--mantine-spacing-sm)",
            borderRadius: "var(--mantine-radius-sm)",
            overflow: "auto",
            marginBottom: "var(--mantine-spacing-sm)",
          },
          "& code": {
            backgroundColor: "var(--mantine-color-gray-1)",
            padding: "2px 4px",
            borderRadius: "var(--mantine-radius-xs)",
            fontSize: "var(--mantine-font-size-sm)",
            fontFamily: "var(--mantine-font-family-monospace)",
          },
          "& pre code": {
            backgroundColor: "transparent",
            padding: 0,
          },
          "& img": {
            maxWidth: "100%",
            height: "auto",
            borderRadius: "var(--mantine-radius-sm)",
          },
          "& table": {
            width: "100%",
            borderCollapse: "collapse",
            marginBottom: "var(--mantine-spacing-md)",
          },
          "& th, & td": {
            padding: "var(--mantine-spacing-xs)",
            borderBottom: "1px solid var(--mantine-color-gray-3)",
            textAlign: "left",
          },
          "& th": {
            fontWeight: 600,
            backgroundColor: "var(--mantine-color-gray-1)",
          },
          "& hr": {
            border: "none",
            borderTop: "1px solid var(--mantine-color-gray-3)",
            margin: "var(--mantine-spacing-lg) 0",
          },
        }}
      />
    </Box>
  );
};
